import { IAccountManagementSegment } from '../../../types/entities/IAccountManagementSegment';
import { IAgency } from '../../../types/entities/IAgency';
import { IAgencyDescription } from '../../../types/entities/IAgencyDescription';
import { IHotelOwnerAgency } from '../../../types/entities/IHotelOwnerAgency';
import { ISupplierHotel } from '../../../types/entities/ISupplierHotel';
import { IZinantisRelationshipStatusType } from '../../../types/entities/IZinantisRelationshipStatusType';
import {
    AccountManagementSegmentState,
    MemberState,
    OwnerZinantisAgenciesState,
    ZinantisMemberDescriptionState,
    ZinantisRelationshipStatusTypeKeys,
    ZinantisStatusState,
} from '../../../types/zinantis/members/membersType';
import { getPastDate } from '../../../utils/formatDates';
import {
    CANCELLED_AS_DISCONNECTED_STATUS_KEY,
    FILTERED_ACCOUNT_MANAGEMENT_SEGMENT_KEY,
    memberDisconnectedStatusColor,
    REJECTED_AS_DISCONNECTED_STATUS_KEY,
} from '../../../utils/zinantis/members/membersUtils';

export const adaptAccountManagementSegmentsToState = (
    segments: IAccountManagementSegment[]
): AccountManagementSegmentState[] => {
    return segments.map(segment => ({
        id: segment?.Id || '',
        name:
            segment?.LocalizedCaption ||
            segment?.Caption ||
            segment?.Name ||
            '',
        key: segment?.Key || '',
    }));
};

export const adaptMembersToState = (members: IAgency[]): MemberState[] => {
    const twoMonthsAgo = getPastDate(2, 'month');
    return members
        .filter(member => {
            return (
                member.AccountManagementSegment.Key !==
                FILTERED_ACCOUNT_MANAGEMENT_SEGMENT_KEY
            );
        })
        .map(member => ({
            id: member?.Id || '',
            name: member?.Name || '',
            address: member?.Address || '',
            country:
                member?.Country?.LocalizedCaption ||
                member?.Country?.Caption ||
                member?.Country?.Name ||
                '',
            idCountry: member?.IdCountry || 0,
            countryCode: member?.Country?.ISO2 || '',
            logo: member?.LogoFile?.VirtualPath || '',
            idAccountManagementSegment: member?.IdAccountManagementSegment || 0,
            accountManagementSegment:
                member?.AccountManagementSegment?.LocalizedCaption ||
                member?.AccountManagementSegment?.Caption ||
                member?.AccountManagementSegment?.Name ||
                '',
            descriptions: [],
            hotels: [],
            allHotels: false,
            signedDate: member?.ZinantisMemberDate || null,
            isNew:
                member?.ZinantisMemberDate &&
                new Date(member?.ZinantisMemberDate) >= new Date(twoMonthsAgo),
            statusKey: ZinantisRelationshipStatusTypeKeys.DISCONNECTED,
            status: 'Disconnected',
            statusColor: memberDisconnectedStatusColor,
            connectionRequests: [],
        }));
};

export const adaptMemberDescriptionToState = (
    description: IAgencyDescription
): ZinantisMemberDescriptionState => {
    return {
        id: description?.Id || '',
        description: description?.Description || '',
        idContentLanguage: description?.IdContentLanguage || 0,
        language:
            description?.ContentLanguage?.LocalizedCaption ||
            description?.ContentLanguage?.Caption ||
            description?.ContentLanguage?.Name ||
            '',
        code: description?.ContentLanguage?.CultureId || '',
    };
};

export const adaptZinantisStatusToState = (
    statuses: Partial<IZinantisRelationshipStatusType>[]
): ZinantisStatusState[] => {
    return statuses
        .map(status => {
            const ddbbStatusText =
                status?.LocalizedCaption ||
                status?.Caption ||
                status?.Name ||
                '';
            const text =
                status?.Key === ZinantisRelationshipStatusTypeKeys.CONFIRMED
                    ? 'connected'
                    : ddbbStatusText;

            return {
                key: status?.Key as ZinantisRelationshipStatusTypeKeys,
                text,
                color: status?.Color || '',
            };
        })
        .concat({
            key: ZinantisRelationshipStatusTypeKeys.DISCONNECTED,
            text: 'disconnected',
            color: memberDisconnectedStatusColor,
        });
};

export const adaptOwnerAgenciesToState = (
    ownerAgencies: IHotelOwnerAgency[],
    supplierHotels: ISupplierHotel[]
): OwnerZinantisAgenciesState[] => {
    return ownerAgencies.map((agency: IHotelOwnerAgency) => {
        const statusTextDDBB =
            agency.ZinantisRelationshipStatusType.LocalizedCaption ||
            agency.ZinantisRelationshipStatusType.Caption ||
            agency.ZinantisRelationshipStatusType.Name ||
            '';
        const statusText =
            agency.ZinantisRelationshipStatusType.Key ===
            ZinantisRelationshipStatusTypeKeys.CONFIRMED
                ? 'connected'
                : statusTextDDBB;

        return {
            id: agency.Id,
            idAgency: agency.IdAgency,
            statusKey: agency.ZinantisRelationshipStatusType
                .Key as ZinantisRelationshipStatusTypeKeys,
            status: statusText,
            statusColor: agency.ZinantisRelationshipStatusType.Color || '',
            descriptions: [],
            hotels: agency.ZinantisRelationships.filter(relationship =>
                supplierHotels.some(
                    supHot => supHot.IdHotel === relationship.IdHotel
                )
            ).map(relationship => {
                const supHotel = supplierHotels.find(
                    supHot => supHot.IdHotel === relationship.IdHotel
                );
                return {
                    id: relationship.Id,
                    idHotel: relationship.IdHotel,
                    hotelName: supHotel?.Name || '',
                    idSupplierHotel: supHotel ? parseInt(supHotel?.Id) : 0,
                    city: supHotel?.Hotel?.City?.Name || '',
                    status:
                        relationship.ZinantisRelationshipStatusType
                            .LocalizedCaption ||
                        relationship.ZinantisRelationshipStatusType.Caption ||
                        relationship.ZinantisRelationshipStatusType.Name ||
                        '',
                    statusKey:
                        relationship.ZinantisRelationshipStatusType.Key || '',
                    statusColor:
                        relationship.ZinantisRelationshipStatusType.Color || '',
                    descriptions: supHotel
                        ? supHotel.Descriptions.map(desc => ({
                              id: desc.Id,
                              description: desc.Description,
                              idContentLanguage: desc.IdContentLanguage || 0,
                              language:
                                  desc.ContentLanguage?.LocalizedCaption ||
                                  desc.ContentLanguage?.Caption ||
                                  desc.ContentLanguage?.Name ||
                                  '',
                              code: desc.ContentLanguage?.Key || '',
                          }))
                        : [],
                };
            }),
            connectionRequests: agency?.ZinantisConnectionRequests.length
                ? agency.ZinantisConnectionRequests.map(request => {
                      return {
                          id: request.Id,
                          requestDate: request.RequestDate,
                          resolutionDate: request.ResolutionDate,
                          requestedBy: request.RequestedBy,
                          statusKey:
                              request.ZinantisConnectionRequestStatus
                                  .ZinantisConnectionRequestStatusType.Key,
                      };
                  })
                : [],
        };
    });
};

export const enrichMembersWithConnectionData = (
    members: MemberState[],
    connectedAgencies: IHotelOwnerAgency[],
    supplierHotels: ISupplierHotel[]
): MemberState[] => {
    return members.map((member: MemberState) => {
        const agency = connectedAgencies
            .filter(
                zinantisAgency =>
                    // puede tener relaciones antiguas que se cancelaron o rechazaron
                    zinantisAgency.ZinantisRelationshipStatusType.Key !==
                        CANCELLED_AS_DISCONNECTED_STATUS_KEY &&
                    zinantisAgency.ZinantisRelationshipStatusType.Key !==
                        REJECTED_AS_DISCONNECTED_STATUS_KEY
            )
            .find(zinantisAgency => {
                return zinantisAgency.IdAgency === parseInt(member.id);
            });

        const hotels = agency
            ? agency.ZinantisRelationships.filter(
                  relationship =>
                      supplierHotels.some(
                          supHot => supHot.IdHotel === relationship.IdHotel
                      ) && agency.AllHotels === false
              ).map(relationship => {
                  const supHotel = supplierHotels.find(
                      supHot => supHot.IdHotel === relationship.IdHotel
                  );

                  return {
                      id: relationship.Id,
                      idHotel: relationship.IdHotel,
                      hotelName: supHotel?.Name || '',
                      country:
                          supHotel?.Hotel?.Country?.LocalizedCaption ||
                          supHotel?.Hotel?.Country?.Caption ||
                          supHotel?.Hotel?.Country?.Name ||
                          '',
                      countryCode: supHotel?.Hotel?.Country?.ISO2 || '',
                      idSupplierHotel: supHotel ? parseInt(supHotel?.Id) : 0,
                      status:
                          relationship.ZinantisRelationshipStatusType
                              .LocalizedCaption ||
                          relationship.ZinantisRelationshipStatusType.Caption ||
                          relationship.ZinantisRelationshipStatusType.Name ||
                          '',
                      statusKey:
                          relationship.ZinantisRelationshipStatusType.Key || '',
                      statusColor:
                          relationship.ZinantisRelationshipStatusType.Color ||
                          '',
                      descriptions: supHotel
                          ? supHotel.Descriptions.map(desc => ({
                                id: desc.Id,
                                description: desc.Description,
                                idContentLanguage: desc.IdContentLanguage || 0,
                                language:
                                    desc.ContentLanguage?.LocalizedCaption ||
                                    desc.ContentLanguage?.Caption ||
                                    desc.ContentLanguage?.Name ||
                                    '',
                                code: desc.ContentLanguage?.Key || '',
                            }))
                          : [],
                      city: supHotel?.Hotel?.City?.Name || '',
                  };
              })
            : [];
        const statusTextDDBB =
            agency?.ZinantisRelationshipStatusType.LocalizedCaption ||
            agency?.ZinantisRelationshipStatusType.Caption ||
            agency?.ZinantisRelationshipStatusType.Name ||
            '';

        const statusText =
            agency?.ZinantisRelationshipStatusType.Key ===
            ZinantisRelationshipStatusTypeKeys.CONFIRMED
                ? 'connected'
                : statusTextDDBB;

        return {
            ...member,
            hotels: hotels,
            allHotels: agency ? agency.AllHotels : false,
            statusKey: agency
                ? (agency.ZinantisRelationshipStatusType
                      .Key as ZinantisRelationshipStatusTypeKeys)
                : ZinantisRelationshipStatusTypeKeys.DISCONNECTED,
            status: agency ? statusText : 'disconnected',
            statusColor: agency
                ? agency.ZinantisRelationshipStatusType.Color
                : memberDisconnectedStatusColor,
            connectionRequests: agency?.ZinantisConnectionRequests.length
                ? agency.ZinantisConnectionRequests.map(request => {
                      return {
                          id: request.Id,
                          requestDate: request.RequestDate,
                          resolutionDate: request.ResolutionDate,
                          requestedBy: request.RequestedBy,
                          statusKey:
                              request.ZinantisConnectionRequestStatus
                                  .ZinantisConnectionRequestStatusType.Key,
                      };
                  })
                : [],
        };
    });
};
